import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  MapPin, Users, Building2, Globe, Heart, ArrowRight, 
  Target, Briefcase, GraduationCap, Sprout, Phone, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

// Regional Office Data
const regionalOffices = {
  'west-africa': {
    name: 'OPAD West Africa',
    region: 'West Africa',
    headquarters: 'Lagos, Nigeria',
    countries: ['Nigeria', 'Ghana', 'Senegal', 'Mali', 'Burkina Faso', 'Ivory Coast', 'Sierra Leone', 'Liberia'],
    established: '2012',
    beneficiaries: '450,000+',
    staff: '85+',
    description: 'OPAD West Africa leads our humanitarian and development programmes across the region, focusing on youth empowerment, climate resilience, and poverty alleviation in some of the most vulnerable communities.',
    programs: [
      { title: 'Youth Skills Development', description: 'Vocational training and employment pathways for young people', icon: GraduationCap },
      { title: 'Climate-Smart Agriculture', description: 'Sustainable farming practices and food security initiatives', icon: Sprout },
      { title: 'Community Health', description: 'Primary healthcare access and disease prevention', icon: Heart },
    ],
    contact: { email: 'westafrica@opad.eu', phone: '+234 XXX XXX XXXX' },
    color: '#E08B7A',
    image: 'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  'east-africa': {
    name: 'OPAD East Africa',
    region: 'East Africa',
    headquarters: 'Addis Ababa, Ethiopia',
    tagline: 'Driving Inclusive Development and Sustainable Impact',
    countries: ['Ethiopia', 'Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'South Sudan', 'Somalia'],
    established: '2010',
    beneficiaries: '680,000+',
    staff: '120+',
    description: 'OPAD East Africa, based in Ethiopia, operates as a dynamic regional platform for inclusive and sustainable development across East Africa. While affiliated with the OPAD global network, the office actively collaborates with governments, civil society, and international partners to advance impactful solutions in poverty reduction, climate resilience, gender equality, youth empowerment, and equitable access to essential services.',
    mission: "To localize OPAD's global development goals in East Africa by promoting poverty alleviation, climate resilience, gender equity, health access, education, and sustainable livelihoods through inclusive, community-driven approaches.",
    strategicPriorities: [
      { title: 'Poverty Alleviation & Livelihood Development', items: ['Implement community-based micro-enterprise programs', 'Strengthen rural entrepreneurship and inclusive value chains'] },
      { title: 'Climate Resilience & Environmental Protection', items: ['Promote climate-smart agriculture and reforestation initiatives', 'Implement community-led disaster preparedness and response systems'] },
      { title: "Gender Equality & Women's Empowerment", items: ["Advance women's participation in leadership and decision-making", 'Expand access to financial literacy and small business grants'] },
      { title: 'Youth Employment & Digital Skills', items: ['Establish digital training hubs for underprivileged youth', 'Facilitate youth innovation and social enterprise competitions'] },
      { title: 'Education & Health Access', items: ['Support rural education infrastructure and inclusive learning', 'Promote maternal and child health awareness campaigns'] },
      { title: 'Humanitarian Aid & Crisis Response', items: ['Develop local emergency response capacity', 'Coordinate with the EU Civil Protection Mechanism and global actors'] }
    ],
    keyActivities: [
      { area: 'Poverty Alleviation & Livelihood Development', objective: 'Strengthen community livelihoods and reduce poverty through inclusive economic opportunities', activities: ['Climate-Smart Model Farms (Q1–Q2, Oromia & Amhara) — Enhances rural productivity, food security, and income generation', 'Support to Rural Entrepreneurship & Value Chains (Ongoing) — Empowers smallholder farmers and micro-enterprises'] },
      { area: 'Climate Resilience & Environmental Protection', objective: 'Build resilient communities capable of adapting to climate change', activities: ['Climate-Smart Agriculture Initiatives (Q1–Q2) — Promotes sustainable farming and environmental conservation', 'Community-Based Disaster Preparedness Programs (Ongoing) — Strengthens local response systems to climate shocks'] },
      { area: "Gender Equality & Women's Empowerment", objective: "Promote gender equity and strengthen women's economic participation", activities: ["Women's Cooperative Start-Up Grants (Q2, SNNPR) — Supports women-led businesses and financial independence", 'Financial Literacy & Leadership Training (Ongoing) — Enhances participation in decision-making'] },
      { area: 'Youth Employment & Digital Skills', objective: 'Increase youth employability through digital innovation and skills development', activities: ['Youth Digital Bootcamps (Q2–Q3, Addis Ababa & Hawassa) — Provides practical digital and entrepreneurial skills', 'Innovation & Social Enterprise Competitions (Ongoing) — Encourages youth-led solutions'] },
      { area: 'Education & Health Access', objective: 'Improve access to quality education and essential health services', activities: ['Public Health Awareness Campaigns (Q3, Tigray & Afar) — Promotes maternal and child health', 'Support to Rural Education Infrastructure (Ongoing) — Expands inclusive learning opportunities'] },
      { area: 'Humanitarian Aid & Crisis Response', objective: 'Strengthen emergency preparedness and response capacity', activities: ['Stakeholder Consultation Forums (Q1, Addis Ababa & Dire Dawa) — Builds humanitarian coordination', 'Regional Conference on Inclusive Development (Q4, Addis Ababa) — Strengthens policy dialogue', 'Emergency Response Capacity Building (Ongoing) — Enhances coordination with EU mechanisms'] }
    ],
    approach: 'OPAD East Africa applies a multi-level, partnership-driven model that connects global expertise, regional coordination, national implementation, and community-led action — ensuring every intervention is locally relevant, sustainable, and scalable.',
    partnerships: ['Government institutions', 'African Union bodies', 'Local NGOs and CBOs', 'International development partners', 'EU and UN agencies'],
    executiveTeam: [
      { name: 'Mesay Tadese', role: 'Regional Director', email: 'mesay@opad.eu', phone: '+251 993 429 152' },
      { name: 'Alexander Adugna', role: 'Deputy Director, Finance and Admin Head', email: 'Alex@oapdeastafrica.eu', phone: '+251 911 693 511' },
      { name: 'Fisehatsion Biadgilign', role: 'Head of Partnership and Business Development', email: 'Fish@opadeastafrica.eu', phone: '+251 910 016 234' },
      { name: 'Demeke Eshete', role: 'Programs Analyst, AU and EU Representative', email: 'Demeke@opadeastafrica.eu', phone: '+251 915 730 685' },
      { name: 'Binyam Nigatu', role: 'Livelihoods and Community Institutions Senior Specialist', email: 'Binyam@opadeastfrica.eu', phone: '+251 912 033 169' },
      { name: 'Misgana Eyob', role: 'Finance Specialist', email: 'Misgana@opadeastafrica.eu', phone: '+251 965 139 709' },
      { name: 'Hanna Gurmessa', role: 'Climate Resilient WASH Analyst', email: 'Hanna@oapdeastafrica.eu', phone: '+251 932 489 739' }
    ],
    programs: [
      { title: 'Emergency Response', description: 'Rapid humanitarian aid for crisis-affected communities', icon: Heart },
      { title: 'Refugee Support', description: 'Integration and livelihood support for displaced populations', icon: Users },
      { title: 'Environmental Resilience', description: 'Climate adaptation and disaster risk reduction', icon: Sprout },
    ],
    contact: { email: 'eastafrica@opad.eu', phone: '+251 993 429 152', director: 'Mesay Tadese' },
    color: '#4A7C59',
    image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  'southern-africa': {
    name: 'OPAD Southern Africa',
    region: 'Southern Africa',
    headquarters: 'Johannesburg, South Africa',
    countries: ['South Africa', 'Zimbabwe', 'Zambia', 'Mozambique', 'Malawi', 'Botswana', 'Namibia'],
    established: '2015',
    beneficiaries: '320,000+',
    staff: '65+',
    description: 'OPAD Southern Africa focuses on economic empowerment, gender equality, and climate action, working with communities to build sustainable livelihoods and resilient futures.',
    programs: [
      { title: 'Women Economic Empowerment', description: 'Microfinance and entrepreneurship for women', icon: Users },
      { title: 'Education Access', description: 'Quality education for underserved communities', icon: GraduationCap },
      { title: 'Sustainable Livelihoods', description: 'Income generation and skills development', icon: Briefcase },
    ],
    contact: { email: 'southernafrica@opad.eu', phone: '+27 XXX XXX XXXX' },
    color: '#E5B75F',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  'europe': {
    name: 'OPAD Europe',
    region: 'Europe',
    headquarters: 'Stockholm, Sweden',
    countries: ['Sweden', 'Belgium', 'Germany', 'United Kingdom', 'France', 'Netherlands', 'Switzerland'],
    established: '2005',
    beneficiaries: 'Global coordination',
    staff: '45+',
    description: 'OPAD Europe serves as our global headquarters and coordination hub, managing partnerships with EU institutions, UN agencies, and international donors while advocating for global development priorities.',
    programs: [
      { title: 'Policy Advocacy', description: 'Engagement with EU and UN policy processes', icon: Building2 },
      { title: 'Donor Relations', description: 'Partnerships with institutional and private donors', icon: Globe },
      { title: 'Research & Learning', description: 'Evidence-based programming and knowledge sharing', icon: Target },
    ],
    contact: { email: 'europe@opad.eu', phone: '+46 XXX XXX XXXX' },
    color: '#7BC4E2',
    image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  'usa': {
    name: 'OPAD USA / North America',
    region: 'North America',
    headquarters: 'Los Angeles, California',
    countries: ['United States', 'Canada'],
    established: '2018',
    beneficiaries: 'Diaspora engagement',
    staff: '25+',
    description: 'OPAD USA engages the North American diaspora, corporate partners, and philanthropists to mobilize resources and awareness for global poverty alleviation and sustainable development.',
    programs: [
      { title: 'Diaspora Engagement', description: 'Connecting communities with development initiatives', icon: Users },
      { title: 'Corporate Partnerships', description: 'CSR collaborations for global impact', icon: Briefcase },
      { title: 'Advocacy & Awareness', description: 'Public education on global development issues', icon: Globe },
    ],
    contact: { email: 'usa@opad.eu', phone: '+1 XXX XXX XXXX', director: 'Dr. Caesar Campos' },
    color: '#E08B7A',
    image: 'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  'asia': {
    name: 'OPAD Asia',
    region: 'Asia Pacific',
    headquarters: 'Beijing, China',
    countries: ['China', 'India', 'Philippines', 'Indonesia', 'Vietnam', 'Thailand'],
    established: '2020',
    beneficiaries: '150,000+',
    staff: '35+',
    description: 'OPAD Asia is our newest regional office, focused on SDGs internship programmes, youth empowerment, and building partnerships with Asian institutions for global development cooperation.',
    programs: [
      { title: 'SDGs Internship Programme', description: 'International development experience for Asian youth', icon: GraduationCap },
      { title: 'South-South Cooperation', description: 'Knowledge exchange between developing regions', icon: Globe },
      { title: 'Innovation Hub', description: 'Technology solutions for development challenges', icon: Target },
    ],
    contact: { email: 'asia@opad.eu', phone: '+86 XXX XXX XXXX', director: 'Ms. Di Wang' },
    color: '#4A7C59',
    image: 'https://images.unsplash.com/photo-1493780474015-ba834fd0ce2f?crop=entropy&cs=srgb&fm=jpg&q=85'
  }
};

export default function RegionalOfficePage({ region }) {
  const office = regionalOffices[region] || regionalOffices['west-africa'];

  return (
    <div className="min-h-screen bg-[#FDF8F4]">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${office.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        
        <div className="container-custom relative z-10 h-full flex items-center">
          <motion.div
            className="max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span 
              className="inline-block text-white text-sm font-semibold px-4 py-2 rounded-full mb-4"
              style={{ backgroundColor: office.color }}
            >
              {office.region} Regional Office
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-4">
              {office.name}
            </h1>
            <p className="text-xl text-white/90 mb-6">
              {office.description}
            </p>
            <div className="flex items-center gap-4 text-white/80">
              <MapPin className="w-5 h-5" />
              <span>Headquarters: {office.headquarters}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold" style={{ color: office.color }}>{office.established}</div>
              <div className="text-slate-500 text-sm">Established</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold" style={{ color: office.color }}>{office.beneficiaries}</div>
              <div className="text-slate-500 text-sm">Beneficiaries</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold" style={{ color: office.color }}>{office.staff}</div>
              <div className="text-slate-500 text-sm">Staff Members</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold" style={{ color: office.color }}>{office.countries.length}</div>
              <div className="text-slate-500 text-sm">Countries</div>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="py-16 bg-[#FDF8F4]">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-8 text-center">
            Countries of Operation
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {office.countries.map((country) => (
              <span 
                key={country}
                className="bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100 text-slate-700 font-medium"
              >
                {country}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-8 text-center">
            Key Programmes
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {office.programs.map((program, index) => (
              <motion.div
                key={program.title}
                className="bg-[#FDF8F4] rounded-2xl p-6 border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${office.color}20` }}
                >
                  <program.icon className="w-7 h-7" style={{ color: office.color }} />
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{program.title}</h3>
                <p className="text-slate-600">{program.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* East Africa Detailed Sections */}
      {office.mission && (
        <section className="py-16 bg-[#FDF8F4]">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-md border border-slate-100">
              <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-3" style={{ color: office.color }}>Our Mission</h2>
              <p className="text-slate-700 leading-relaxed">{office.mission}</p>
            </div>
          </div>
        </section>
      )}

      {office.strategicPriorities && (
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-8 text-center">
              Strategic Objectives & Priority Areas
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
              {office.strategicPriorities.map((priority, idx) => (
                <motion.div
                  key={priority.title}
                  className="bg-[#FDF8F4] rounded-2xl p-6 border border-slate-100"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: office.color }}>{idx + 1}</span>
                    <h3 className="font-bold text-slate-900">{priority.title}</h3>
                  </div>
                  <ul className="space-y-2 text-slate-600 text-sm pl-11">
                    {priority.items.map((item) => (
                      <li key={item} className="list-disc">{item}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {office.keyActivities && (
        <section className="py-16 bg-[#FDF8F4]">
          <div className="container-custom">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-8 text-center">
              Key Activities
            </h2>
            <div className="space-y-6 max-w-5xl mx-auto">
              {office.keyActivities.map((act, idx) => (
                <motion.div
                  key={act.area}
                  className="bg-white rounded-2xl p-6 shadow-sm border-l-4"
                  style={{ borderColor: office.color }}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <h3 className="font-bold text-slate-900 mb-2" style={{ color: office.color }}>{act.area}</h3>
                  <p className="text-sm text-slate-500 italic mb-3"><strong>Objective:</strong> {act.objective}</p>
                  <ul className="space-y-2 text-slate-700 text-sm list-disc pl-5">
                    {act.activities.map((a) => <li key={a}>{a}</li>)}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {office.approach && (
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 items-start">
              <div className="bg-[#FDF8F4] rounded-2xl p-8 border border-slate-100">
                <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-3">Our Approach in {office.region}</h3>
                <p className="text-slate-700 leading-relaxed">{office.approach}</p>
              </div>
              {office.partnerships && (
                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-4">Partnerships & Collaboration</h3>
                  <ul className="space-y-2">
                    {office.partnerships.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-slate-700">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: office.color }} />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {office.executiveTeam && (
        <section className="py-16 bg-[#FDF8F4]">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-3">
                {office.region} Executive Team
              </h2>
              <p className="text-slate-600">Meet the leadership driving impact across the region.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {office.executiveTeam.map((member, idx) => (
                <motion.div
                  key={member.email}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  data-testid={`east-africa-team-${idx}`}
                >
                  <h3 className="font-bold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-sm font-medium mb-3" style={{ color: office.color }}>{member.role}</p>
                  <div className="space-y-1 text-sm text-slate-600">
                    <a href={`mailto:${member.email}`} className="flex items-center gap-2 hover:text-slate-900 break-all">
                      <Mail className="w-4 h-4 flex-shrink-0" style={{ color: office.color }} />
                      {member.email}
                    </a>
                    <a href={`tel:${member.phone}`} className="flex items-center gap-2 hover:text-slate-900">
                      <Phone className="w-4 h-4 flex-shrink-0" style={{ color: office.color }} />
                      {member.phone}
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-16" style={{ backgroundColor: `${office.color}10` }}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Contact {office.name}
            </h2>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              {office.contact.director && (
                <p className="text-slate-600 mb-4">
                  <strong>Regional Director:</strong> {office.contact.director}
                </p>
              )}
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                <a href={`mailto:${office.contact.email}`} className="flex items-center gap-2 text-slate-700 hover:text-slate-900">
                  <Mail className="w-5 h-5" style={{ color: office.color }} />
                  {office.contact.email}
                </a>
                <a href={`tel:${office.contact.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-slate-900">
                  <Phone className="w-5 h-5" style={{ color: office.color }} />
                  {office.contact.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-white mb-4">
            Support Our Work in {office.region}
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Your donation helps us continue delivering life-changing programmes to communities across {office.region}.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="rounded-full px-8 py-6 text-lg font-semibold" style={{ backgroundColor: office.color }}>
                <Heart className="w-5 h-5 mr-2" />
                Donate Now
              </Button>
            </a>
            <Link to="/get-involved">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-lg font-semibold">
                Volunteer With Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Export individual page components
export const WestAfricaPage = () => <RegionalOfficePage region="west-africa" />;
export const EastAfricaPage = () => <RegionalOfficePage region="east-africa" />;
export const SouthernAfricaPage = () => <RegionalOfficePage region="southern-africa" />;
export const EuropePage = () => <RegionalOfficePage region="europe" />;
export const USAPage = () => <RegionalOfficePage region="usa" />;
export const AsiaPage = () => <RegionalOfficePage region="asia" />;
